import React, { useState, useEffect } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../firebase';
import Header from './Header';
import Footer from './Footer';

// Helper function to remove token from Firebase Storage URLs
const stripToken = (url) => {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('token');
    return urlObj.toString();
  } catch {
    return url.replace(/&token=[^&]+/, '').replace(/\?token=[^&]+&?/, '?');
  }
};

const Gallery = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const galleryRef = ref(database, 'school/gallery');
    
    const handleData = (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const itemsArray = Object.entries(data).map(([id, item]) => ({
          id,
          title: item.title || 'Untitled',
          description: item.description || '',
          category: item.category || 'uncategorized',
          createdAt: item.createdAt || new Date().toISOString(),
          mediaType: item.mediaType || 'photo',
          files: item.files ? item.files : (item.mediaUrl ? [{ url: item.mediaUrl, type: item.mediaType || 'photo' }] : []),
          // Extract the first file URL if available and strip token
          mediaUrl: item.files && item.files[0] ? stripToken(item.files[0].url) : (item.mediaUrl ? stripToken(item.mediaUrl) : null),
          // Determine if it's a video by file type or extension
          isVideo: (item.files && item.files[0] && 
                   (item.files[0].type === 'video' || 
                    (item.files[0].url && item.files[0].url.match(/\.(mp4|webm|mov)$/i)))) || (item.mediaType === 'video')
        }));
        setMediaItems(itemsArray);
        setFilteredMedia(itemsArray);
      }
      setLoading(false);
    };

    onValue(galleryRef, handleData);
    
    // Cleanup function
    return () => {
      off(galleryRef, 'value', handleData);
    };
  }, []);

  const filterMedia = (category) => {
    setActiveCategory(category);
    if (category === 'all') {
      setFilteredMedia(mediaItems);
    } else {
      const filtered = mediaItems.filter(item => item.category === category);
      setFilteredMedia(filtered);
    }
  };

  const openModal = (item, fileIndex = 0) => {
    setSelectedItem(item);
    setCurrentFileIndex(fileIndex);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedItem(null);
    document.body.style.overflow = 'auto';
  };

  // Extract unique categories
  const categories = ['all', ...new Set(mediaItems.map(item => item.category).filter(Boolean))];

  return (
    <>
    <style>
      {`
        .gallery-scroll-container::-webkit-scrollbar {
          display: none;
        }
      `}
    </style>
    <Header/>
    <div style={styles.container}>
  <div style={styles.heroSection}>
    <h1 style={styles.header}>School Memories Gallery</h1>
    <p style={styles.subHeader}>Relive the moments that make our school special</p>
  </div>
  
  {loading ? (
    <div style={styles.loadingContainer}>
      <div style={styles.spinnerContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Curating your gallery experience...</p>
      </div>
    </div>
  ) : (
    <>
      {/* Category Filter with Search */}
      <div style={styles.filterSection}>        
        <div style={styles.filterScroll}>
          {[ ...categories].map(category => (
            <button
              key={category}
              style={{
                ...styles.filterButton,
                ...(activeCategory === category ? styles.activeFilter : {})
              }}
              onClick={() => filterMedia(category)}
            >
              {category}
              {activeCategory === category && <span style={styles.activeIndicator}></span>}
            </button>
          ))}
        </div>
      </div>
      
      {/* Media Grid */}
      <div style={styles.galleryGrid}>
        {filteredMedia.map(item => (
          item.files && item.files.length > 0 && (
            <div 
              key={item.id} 
              style={styles.galleryItem}
            >
              <div 
                className="gallery-scroll-container"
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  scrollSnapType: 'x mandatory',
                  width: '100%',
                  height: '100%',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {item.files.map((file, idx) => {
                  const url = stripToken(file.url);
                  const isVid = file.type === 'video' || (url && url.match(/\.(mp4|webm|mov)$/i));
                  return (
                    <div 
                      key={idx} 
                      style={{ flex: '0 0 100%', scrollSnapAlign: 'start', position: 'relative', cursor: 'pointer' }}
                      onClick={() => openModal(item, idx)}
                    >
                      {isVid ? (
                        <>
                          <video 
                            style={styles.galleryMedia}
                            muted
                            playsInline
                            preload="metadata"
                          >
                            <source src={url} type="video/mp4" />
                          </video>
                          <div style={styles.videoOverlay}>
                            <div style={styles.playButton}>
                              <div style={styles.playTriangle}></div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <img 
                          src={url} 
                          alt={`${item.title} - ${idx + 1}`} 
                          style={styles.galleryMedia}
                          loading="lazy"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Multiple images indicator */}
              {item.files.length > 1 && (
                <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'none' }}>
                  <span style={{ fontSize: '1rem' }}>⧉</span> {item.files.length}
                </div>
              )}

              <div style={styles.itemOverlay} onClick={() => openModal(item, 0)}>
                <div style={styles.itemInfo}>
                  <h3 style={styles.itemTitle}>{item.title}</h3>
                  <div style={styles.metaContainer}>
                    <span style={styles.itemCategory}>{item.category}</span>
                    <span style={styles.itemDate}>
                      {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                {/* Removed top right mediaTypeBadge to prevent overlap with multi-image indicator */}
              </div>
            </div>
          )
        ))}
      </div>
      
      {/* Empty State */}
      {filteredMedia.length === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIllustration}>📷</div>
          <h3 style={styles.emptyTitle}>No media found</h3>
          <p style={styles.emptyText}>Try changing your search or filter criteria</p>
          <button 
            style={styles.resetButton}
            onClick={() => {
              setActiveCategory('All');
              setSearchQuery('');
            }}
          >
            Reset Filters
          </button>
        </div>
      )}
      
      {/* Modal for selected media */}
      {selectedItem && selectedItem.files && selectedItem.files[currentFileIndex] && (() => {
        const currentFile = selectedItem.files[currentFileIndex];
        const currentUrl = stripToken(currentFile.url);
        const isCurrentVideo = currentFile.type === 'video' || (currentUrl && currentUrl.match(/\.(mp4|webm|mov)$/i));
        
        return (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={closeModal}>
              <span style={styles.closeIcon}>×</span>
            </button>
            
            <div style={{ ...styles.modalMediaContainer, position: 'relative' }}>
              {selectedItem.files.length > 1 && (
                <button 
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '20px', cursor: 'pointer', zIndex: 10 }}
                  onClick={(e) => { e.stopPropagation(); setCurrentFileIndex(prev => prev === 0 ? selectedItem.files.length - 1 : prev - 1); }}
                >
                  &#10094;
                </button>
              )}

              {isCurrentVideo ? (
                <video 
                  controls
                  autoPlay
                  style={styles.modalMedia}
                  key={currentUrl}
                >
                  <source src={currentUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={currentUrl} 
                  alt={selectedItem.title} 
                  style={styles.modalMedia}
                />
              )}

              {selectedItem.files.length > 1 && (
                <button 
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '20px', cursor: 'pointer', zIndex: 10 }}
                  onClick={(e) => { e.stopPropagation(); setCurrentFileIndex(prev => prev === selectedItem.files.length - 1 ? 0 : prev + 1); }}
                >
                  &#10095;
                </button>
              )}

              {selectedItem.files.length > 1 && (
                <div style={{ position: 'absolute', bottom: '15px', display: 'flex', gap: '8px', justifyContent: 'center', width: '100%' }}>
                  {selectedItem.files.map((_, idx) => (
                    <div key={idx} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: idx === currentFileIndex ? 'white' : 'rgba(255,255,255,0.5)', transition: 'background-color 0.3s' }} />
                  ))}
                </div>
              )}
            </div>
            
            <div style={styles.modalDetails}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{selectedItem.title}</h2>
                <div style={styles.modalBadges}>
                  <span style={styles.modalCategoryBadge}>
                    {selectedItem.category}
                  </span>
                  <span style={styles.modalTypeBadge}>
                    {isCurrentVideo ? 'Video' : 'Photo'}
                  </span>
                </div>
              </div>
              
              <p style={styles.modalDescription}>{selectedItem.description}</p>
              
              <div style={styles.modalFooter}>
                <div style={styles.dateContainer}>
                  <span style={styles.calendarIcon}>📅</span>
                  <span style={styles.modalDate}>
                    {new Date(selectedItem.createdAt).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                
                <div style={styles.socialContainer}>
                  <button style={styles.socialButton}>
                    <span style={styles.socialIcon}>👍</span>
                    <span>Like</span>
                  </button>
                  <button style={styles.socialButton}>
                    <span style={styles.socialIcon}>💬</span>
                    <span>Comment</span>
                  </button>
                  <button style={styles.socialButton}>
                    <span style={styles.socialIcon}>↗️</span>
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  )}
</div>
<Footer/>
    </>
  );
};

// const styles = {
//   container: {
//     maxWidth: '1200px',
//     margin: '0 auto',
//     padding: '20px',
//     fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
//     backgroundColor: '#f9f9f9',
//     minHeight: '100vh'
//   },
//   header: {
//     textAlign: 'center',
//     color: '#2c3e50',
//     marginBottom: '30px',
//     fontSize: '2.5rem',
//     fontWeight: '300',
//     borderBottom: '2px solid #3498db',
//     paddingBottom: '10px',
//     display: 'inline-block',
//     marginLeft: '50%',
//     transform: 'translateX(-50%)'
//   },
//   loading: {
//     display: 'flex',
//     flexDirection: 'column',
//     alignItems: 'center',
//     justifyContent: 'center',
//     height: '300px',
//     color: '#7f8c8d'
//   },
//   spinner: {
//     border: '5px solid #f3f3f3',
//     borderTop: '5px solid #3498db',
//     borderRadius: '50%',
//     width: '50px',
//     height: '50px',
//     animation: 'spin 1s linear infinite',
//     marginBottom: '20px'
//   },
//   filterContainer: {
//     display: 'flex',
//     justifyContent: 'center',
//     flexWrap: 'wrap',
//     marginBottom: '30px',
//     gap: '10px'
//   },
//   filterButton: {
//     padding: '8px 16px',
//     backgroundColor: '#ecf0f1',
//     border: 'none',
//     borderRadius: '20px',
//     cursor: 'pointer',
//     fontSize: '14px',
//     fontWeight: '600',
//     color: '#7f8c8d',
//     transition: 'all 0.3s ease',
//     outline: 'none'
//   },
//   activeFilter: {
//     backgroundColor: '#3498db',
//     color: 'white'
//   },
//   galleryGrid: {
//     display: 'grid',
//     gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
//     gap: '20px',
//     padding: '10px'
//   },
//   galleryItem: {
//     position: 'relative',
//     borderRadius: '8px',
//     overflow: 'hidden',
//     boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
//     transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//     cursor: 'pointer',
//     aspectRatio: '1 / 1',
//     ':hover': {
//       transform: 'translateY(-5px)',
//       boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
//     }
//   },
//   galleryMedia: {
//     width: '100%',
//     height: '100%',
//     objectFit: 'cover',
//     display: 'block'
//   },
//   videoOverlay: {
//     position: 'absolute',
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     backgroundColor: 'rgba(0,0,0,0.3)'
//   },
//   playIcon: {
//     fontSize: '50px',
//     color: 'rgba(255,255,255,0.7)',
//     textShadow: '0 0 10px rgba(0,0,0,0.5)'
//   },
//   imageOverlay: {
//     position: 'absolute',
//     bottom: '0',
//     left: '0',
//     right: '0',
//     backgroundColor: 'rgba(0,0,0,0.7)',
//     padding: '10px',
//     color: 'white'
//   },
//   imageTitle: {
//     margin: '0',
//     fontSize: '16px',
//     fontWeight: '500'
//   },
//   imageCategory: {
//     margin: '5px 0 0',
//     fontSize: '14px',
//     color: '#bdc3c7'
//   },
//   mediaType: {
//     margin: '5px 0 0',
//     fontSize: '12px',
//     color: '#3498db',
//     fontWeight: 'bold'
//   },
//   modalOverlay: {
//     position: 'fixed',
//     top: '0',
//     left: '0',
//     right: '0',
//     bottom: '0',
//     backgroundColor: 'rgba(0,0,0,0.9)',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     zIndex: '1000',
//     padding: '20px'
//   },
//   modalContent: {
//     position: 'relative',
//     maxWidth: '900px',
//     width: '90%',
//     backgroundColor: 'white',
//     borderRadius: '10px',
//     overflow: 'hidden',
//     display: 'flex',
//     flexDirection: 'column',
//     maxHeight: '90vh'
//   },
//   modalMedia: {
//     width: '100%',
//     maxHeight: '60vh',
//     objectFit: 'contain',
//     backgroundColor: '#f1f1f1'
//   },
//   modalDetails: {
//     padding: '20px',
//     overflowY: 'auto'
//   },
//   modalTitle: {
//     marginTop: '0',
//     color: '#2c3e50'
//   },
//   modalCategory: {
//     color: '#3498db',
//     fontWeight: '600',
//     margin: '5px 0'
//   },
//   modalType: {
//     color: '#e74c3c',
//     fontWeight: '600',
//     margin: '5px 0'
//   },
//   modalDescription: {
//     color: '#7f8c8d',
//     lineHeight: '1.6'
//   },
//   modalDate: {
//     color: '#bdc3c7',
//     fontSize: '14px',
//     marginTop: '10px'
//   },
//   closeButton: {
//     position: 'absolute',
//     top: '10px',
//     right: '15px',
//     fontSize: '30px',
//     color: '#2c3e50',
//     background: 'none',
//     border: 'none',
//     cursor: 'pointer',
//     zIndex: '1001',
//     transition: 'color 0.3s ease',
//     ':hover': {
//       color: '#e74c3c'
//     }
//   },
//   '@keyframes spin': {
//     '0%': { transform: 'rotate(0deg)' },
//     '100%': { transform: 'rotate(360deg)' }
//   }
// };

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px 60px',
    fontFamily: "'Poppins', sans-serif",
    backgroundColor: '#ffffff',
    minHeight: '100vh'
  },
  
  // Hero Section
  heroSection: {
    textAlign: 'center',
    padding: '40px 20px 40px',
    background: 'linear-gradient(135deg, #4a6fdc 0%, #0f2b6e 100%)',
    color: 'white',
    marginBottom: '40px',
    marginTop: '20px',
    borderRadius: '0 0 20px 20px',
    boxShadow: '0 10px 30px rgba(15, 43, 110, 0.2)'
  },
  header: {
    fontSize: '2.8rem',
    fontWeight: '700',
    marginBottom: '10px',
    background: 'linear-gradient(90deg, #ffffff, #f9a826)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  subHeader: {
    fontSize: '1.2rem',
    fontWeight: '300',
    opacity: '0.9',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6'
  },
  
  // Loading State
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  },
  spinnerContainer: {
    textAlign: 'center'
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '5px solid rgba(15, 43, 110, 0.1)',
    borderTop: '5px solid #4a6fdc',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    color: '#4a6fdc',
    fontSize: '1.1rem',
    fontWeight: '500'
  },
  
  // Filter Section
  filterSection: {
    marginBottom: '40px',
    padding: '0 10px'
  },
  searchContainer: {
    position: 'relative',
    maxWidth: '500px',
    margin: '0 auto 25px'
  },
  searchInput: {
    width: '100%',
    padding: '12px 20px 12px 45px',
    borderRadius: '30px',
    border: '1px solid #e0e0e0',
    fontSize: '1rem',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#7f8c8d',
    fontSize: '1.2rem'
  },
  filterScroll: {
    display: 'flex',
    gap: '10px',
    overflowX: 'auto',
    paddingBottom: '10px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch'
  },
  filterButton: {
    padding: '8px 20px',
    backgroundColor: '#f5f7fa',
    border: 'none',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#5a6c7d',
    transition: 'all 0.3s ease',
    outline: 'none',
    whiteSpace: 'nowrap',
    position: 'relative'
  },
  activeFilter: {
    backgroundColor: '#4a6fdc',
    color: 'white',
    boxShadow: '0 4px 12px rgba(74, 111, 220, 0.3)'
  },
  activeIndicator: {
    position: 'absolute',
    bottom: '-5px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '3px',
    backgroundColor: 'white',
    borderRadius: '3px'
  },
  
  // Gallery Grid
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '25px',
    padding: '10px'
  },
  galleryItem: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    aspectRatio: '1 / 1'
  },
  galleryMedia: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  
  // Video Specific Styles
  videoOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.1)'
  },
  playButton: {
    width: '60px',
    height: '60px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  },
  playTriangle: {
    width: '0',
    height: '0',
    borderTop: '12px solid transparent',
    borderLeft: '20px solid #4a6fdc',
    borderBottom: '12px solid transparent',
    marginLeft: '5px'
  },
  durationBadge: {
    position: 'absolute',
    bottom: '15px',
    right: '15px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '500'
  },
  
  // Item Overlay
  itemOverlay: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    backgroundColor: 'rgba(0,0,0,0.8)',
    padding: '15px',
    color: 'white',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.3s ease'
  },
  itemInfo: {
    marginBottom: '10px'
  },
  itemTitle: {
    margin: '0 0 5px 0',
    fontSize: '1.1rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  metaContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  itemCategory: {
    fontSize: '0.85rem',
    color: '#bdc3c7',
    backgroundColor: 'rgba(74, 111, 220, 0.2)',
    padding: '3px 10px',
    borderRadius: '12px'
  },
  itemDate: {
    fontSize: '0.8rem',
    color: '#bdc3c7'
  },
  mediaTypeBadge: {
    position: 'absolute',
    top: '-15px',
    right: '15px',
    backgroundColor: '#4a6fdc',
    color: 'white',
    padding: '5px 12px',
    borderRadius: '15px',
    fontSize: '0.8rem',
    fontWeight: '600',
    boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
  },
  
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    gridColumn: '1 / -1'
  },
  emptyIllustration: {
    fontSize: '4rem',
    marginBottom: '20px',
    opacity: '0.5'
  },
  emptyTitle: {
    color: '#2c3e50',
    fontSize: '1.5rem',
    marginBottom: '10px'
  },
  emptyText: {
    color: '#7f8c8d',
    fontSize: '1rem',
    marginBottom: '20px'
  },
  resetButton: {
    backgroundColor: '#4a6fdc',
    color: 'white',
    border: 'none',
    padding: '10px 25px',
    borderRadius: '25px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0,0,0,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
    padding: '20px',
    backdropFilter: 'blur(5px)'
  },
  modalContent: {
    position: 'relative',
    maxWidth: '1000px',
    width: '90%',
    backgroundColor: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
  },
  closeButton: {
    position: 'absolute',
    top: '15px',
    right: '20px',
    width: '40px',
    height: '40px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    fontSize: '1.5rem',
    cursor: 'pointer',
    zIndex: '1001',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  },
  closeIcon: {
    marginTop: '-2px'
  },
  modalMediaContainer: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f1f1',
    maxHeight: '60vh',
    overflow: 'hidden'
  },
  modalMedia: {
    width: '100%',
    height: 'auto',
    maxHeight: '60vh',
    objectFit: 'contain'
  },
  modalDetails: {
    padding: '25px',
    overflowY: 'auto'
  },
  modalHeader: {
    marginBottom: '20px'
  },
  modalTitle: {
    margin: '0 0 10px 0',
    color: '#2c3e50',
    fontSize: '1.8rem',
    fontWeight: '600'
  },
  modalBadges: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px'
  },
  modalCategoryBadge: {
    backgroundColor: 'rgba(74, 111, 220, 0.1)',
    color: '#4a6fdc',
    padding: '5px 12px',
    borderRadius: '15px',
    fontSize: '0.85rem',
    fontWeight: '600'
  },
  modalTypeBadge: {
    backgroundColor: 'rgba(233, 30, 99, 0.1)',
    color: '#e91e63',
    padding: '5px 12px',
    borderRadius: '15px',
    fontSize: '0.85rem',
    fontWeight: '600'
  },
  modalDescription: {
    color: '#5a6c7d',
    lineHeight: '1.7',
    fontSize: '1rem',
    marginBottom: '25px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #f0f0f0',
    paddingTop: '20px'
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  calendarIcon: {
    fontSize: '1.1rem'
  },
  modalDate: {
    color: '#7f8c8d',
    fontSize: '0.9rem'
  },
  socialContainer: {
    display: 'flex',
    gap: '10px'
  },
  socialButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    backgroundColor: '#f5f7fa',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  socialIcon: {
    fontSize: '1rem'
  },
  
  // Animations
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' }
  }
};


export default Gallery;